/**
 * Comment surfaces — the indicator drawn on a commented Gantt bar, the
 * read-only block folded into a bar's hover pop-up, the thread itself (list +
 * composer), and the popover the Gantt opens that thread in.
 *
 * Used by both Gantt implementations (ScheduleFullGantt's Timeline and By
 * Product tabs, FactoryScheduleView's TimelineTab) so the two can't drift, and
 * by the downtime windows dialog, which hosts `CommentThread` inline rather
 * than in a popover. Anchoring rules live in `ScheduleComments.anchor.ts`.
 */

import { memo, useCallback, useEffect, useRef, useState } from 'react'
import {
  Box,
  Checkbox,
  CircularProgress,
  IconButton,
  Popover,
  TextField,
  Tooltip,
} from '@mui/material'
import ChatBubbleIcon from '@mui/icons-material/ChatBubble'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import toast from 'react-hot-toast'
import {
  FONT_MONO,
  FONT_SANS,
  PillButton,
  RADII,
  TOKENS,
} from './SchedulerDesign'
import {
  SchedulerComment,
  useCreateComment,
  useDeleteComment,
  useUpdateComment,
} from './Scheduler.data'
import {
  CommentTarget,
  commentInitials,
  commentTimeAgo,
  commentWindowMoved,
  formatWeekHour,
} from './ScheduleComments.anchor'

/** Accent for anything comment-related, so the mark reads as one thing
 *  wherever it appears. Distinct from every `TOKENS.gantt*` bar fill. */
export const COMMENT_ACCENT = '#7A4FBF'

/** The dot on a DOWNTIME window, which is red rather than the accent above.
 *  A downtime block is hatched, small and easy to skim past, and the note on
 *  one is usually the reason the plant is down — so it is marked in the
 *  downtime colour itself (`TOKENS.ganttDowntime`, the same red as the
 *  hatching) rather than in the generic comment purple. */
export const COMMENT_ACCENT_DOWNTIME = TOKENS.ganttDowntime

// ─── CommentDot ──────────────────────────────────────────────────────────────

/**
 * The "there is a comment here" mark, drawn inside the top-right corner of a
 * bar. A white ring carries it against every bar fill without hiding the
 * bar's own label, which is centred or left-set. Muted once every comment on
 * the bar is resolved — the mark stays, because the comment is still there to
 * read, but stops competing for attention.
 *
 * **10px, raised from 6px (2026-09-21, client request.)** At 6 it was a
 * speck: findable once you knew a comment was there, invisible when scanning
 * a week of bars for one. The ring grew with it (1.5px → 2px) because the
 * halo is what separates the mark from the fill, and a bigger dot inside the
 * same thin ring reads as a smudge on the darker bars.
 *
 * This is about as large as it can go. The shortest bars on a busy week are
 * only a few pixels wide, and past roughly 10px the mark stops annotating a
 * bar and starts replacing it.
 */
export const CommentDot = memo(
  ({
    allResolved = false,
    accent = COMMENT_ACCENT,
  }: {
    allResolved?: boolean
    /** Override the fill — `COMMENT_ACCENT_DOWNTIME` on a downtime window.
     *  Resolved still greys out whatever the accent, because "dealt with"
     *  means the same thing on every kind of bar. */
    accent?: string
  }) => (
    <Box
      sx={{
        position: 'absolute',
        top: '2px',
        right: '2px',
        width: 10,
        height: 10,
        borderRadius: '50%',
        bgcolor: allResolved ? TOKENS.ink300 : accent,
        boxShadow: '0 0 0 2px rgba(255,255,255,0.92)',
        pointerEvents: 'none',
        zIndex: 5,
      }}
    />
  )
)
CommentDot.displayName = 'CommentDot'

// ─── CommentHoverBlock ───────────────────────────────────────────────────────

/**
 * The comment half of a bar's hover pop-up. Both hover boxes it is used in are
 * dark, so it is styled for a dark ground only.
 *
 * `onAdd` is omitted where the hover box can't take a click — the
 * FactoryScheduleView tip follows the mouse with `pointerEvents: 'none'`, so
 * there it prints the affordance and the bar's own click opens the popover.
 */
export const CommentHoverBlock = memo(
  ({
    comments,
    onAdd,
    readOnly = false,
  }: {
    comments: SchedulerComment[]
    /** Receives the click so the popover can be positioned where the user
     *  actually pressed — see `ScheduleCommentPopoverProps.position`. */
    onAdd?: (e: React.MouseEvent<HTMLElement>) => void
    readOnly?: boolean
  }) => {
    const unresolved = comments.filter((c) => !c.resolved)
    const shown = (unresolved.length ? unresolved : comments).slice(0, 2)
    const hidden = comments.length - shown.length

    return (
      <Box
        sx={{
          mt: '7px',
          pt: '6px',
          borderTop: '1px solid rgba(255,255,255,0.14)',
        }}
      >
        {comments.length > 0 && (
          <>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '5px',
                mb: '4px',
              }}
            >
              <ChatBubbleIcon sx={{ fontSize: 9, color: COMMENT_ACCENT }} />
              <Box
                sx={{
                  fontSize: 9.5,
                  fontWeight: 700,
                  letterSpacing: '0.04em',
                  textTransform: 'uppercase',
                  color: 'rgba(255,255,255,0.62)',
                }}
              >
                {comments.length === 1
                  ? '1 comment'
                  : `${comments.length} comments`}
              </Box>
            </Box>
            {shown.map((c) => (
              <Box key={c.id} sx={{ mb: '3px' }}>
                <Box
                  sx={{
                    fontSize: 10,
                    color: 'rgba(255,255,255,0.88)',
                    lineHeight: 1.35,
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                    textDecoration: c.resolved ? 'line-through' : 'none',
                  }}
                >
                  {c.content}
                </Box>
                <Box sx={{ fontSize: 9, color: 'rgba(255,255,255,0.42)' }}>
                  {c.authorName || c.authorEmail} ·{' '}
                  {commentTimeAgo(c.createdAt)}
                </Box>
              </Box>
            ))}
            {hidden > 0 && (
              <Box sx={{ fontSize: 9, color: 'rgba(255,255,255,0.42)' }}>
                +{hidden} more
              </Box>
            )}
          </>
        )}
        {onAdd ? (
          <Box
            component="button"
            type="button"
            onClick={onAdd}
            sx={{
              mt: comments.length ? '5px' : 0,
              display: 'inline-flex',
              alignItems: 'center',
              gap: '4px',
              px: '6px',
              py: '3px',
              border: '1px solid rgba(255,255,255,0.24)',
              borderRadius: RADII.sm,
              bgcolor: 'transparent',
              color: '#fff',
              fontFamily: FONT_SANS,
              fontSize: 9.5,
              fontWeight: 600,
              cursor: 'pointer',
              '&:hover': { bgcolor: 'rgba(255,255,255,0.12)' },
            }}
          >
            <ChatBubbleIcon sx={{ fontSize: 9 }} />
            {comments.length
              ? 'Open comments'
              : readOnly
                ? 'View comments'
                : 'Add comment'}
          </Box>
        ) : (
          <Box
            sx={{
              mt: comments.length ? '5px' : 0,
              fontSize: 9,
              color: 'rgba(255,255,255,0.5)',
              fontStyle: 'italic',
            }}
          >
            {comments.length
              ? 'Click the bar to open comments'
              : 'Click the bar to add a comment'}
          </Box>
        )}
      </Box>
    )
  }
)
CommentHoverBlock.displayName = 'CommentHoverBlock'

// ─── One comment in the thread ───────────────────────────────────────────────

const ThreadComment = memo(
  ({
    comment,
    target,
    isOwn,
    readOnly,
    onResolve,
    onDelete,
  }: {
    comment: SchedulerComment
    target: CommentTarget
    isOwn: boolean
    readOnly: boolean
    onResolve: (id: string, resolved: boolean) => void
    onDelete: (id: string) => void
  }) => {
    const moved = commentWindowMoved(comment, target)
    return (
      <Box
        sx={{
          p: '8px 9px',
          borderRadius: RADII.sm,
          border: `1px solid ${TOKENS.line200}`,
          bgcolor: comment.resolved ? TOKENS.surface50 : TOKENS.surface0,
          opacity: comment.resolved ? 0.72 : 1,
        }}
      >
        <Box
          sx={{ display: 'flex', alignItems: 'center', gap: '6px', mb: '5px' }}
        >
          <Box
            sx={{
              width: 20,
              height: 20,
              borderRadius: '50%',
              bgcolor: COMMENT_ACCENT,
              color: '#fff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 8.5,
              fontWeight: 700,
              flexShrink: 0,
            }}
          >
            {commentInitials(comment.authorName || comment.authorEmail)}
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box
              sx={{
                fontSize: 11,
                fontWeight: 600,
                color: TOKENS.ink900,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {comment.authorName || comment.authorEmail || 'Unknown user'}
            </Box>
            <Box sx={{ fontSize: 9.5, color: TOKENS.ink400 }}>
              {commentTimeAgo(comment.createdAt)}
            </Box>
          </Box>
          {!readOnly && (
            <Tooltip
              title={comment.resolved ? 'Mark unresolved' : 'Mark resolved'}
            >
              <Checkbox
                checked={comment.resolved}
                onChange={() => onResolve(comment.id, !comment.resolved)}
                size="small"
                sx={{ p: 0.25, '& .MuiSvgIcon-root': { fontSize: 15 } }}
              />
            </Tooltip>
          )}
          {!readOnly && isOwn && (
            <Tooltip title="Delete">
              <IconButton
                size="small"
                onClick={() => onDelete(comment.id)}
                sx={{ p: 0.25 }}
              >
                <DeleteOutlineIcon
                  sx={{ fontSize: 14, color: TOKENS.ink400 }}
                />
              </IconButton>
            </Tooltip>
          )}
        </Box>
        <Box
          sx={{
            fontSize: 11.5,
            color: TOKENS.ink700,
            lineHeight: 1.4,
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            textDecoration: comment.resolved ? 'line-through' : 'none',
          }}
        >
          {comment.content}
        </Box>
        {moved && (
          <Box
            sx={{
              mt: '5px',
              fontSize: 9.5,
              color: TOKENS.amberFg,
              fontFamily: FONT_MONO,
            }}
          >
            written against {formatWeekHour(comment.startTime)}–
            {formatWeekHour(comment.endTime)} — the batch has moved since
          </Box>
        )}
      </Box>
    )
  }
)
ThreadComment.displayName = 'ThreadComment'

// ─── CommentThread ───────────────────────────────────────────────────────────

export type CommentThreadProps = {
  schedulerId: string
  iteration: string
  weekNumber: number
  /** What the thread is against. Its `kind` decides how the comment is
   *  matched back — see `commentAnchorKey`. */
  target: CommentTarget
  comments: SchedulerComment[]
  /** Reading stays available; only posting, resolving and deleting stop. */
  readOnly?: boolean
  readOnlyReason?: string
  currentUserEmail: string
  emptyText?: string
  /** Cap on the scrolling list. The popover is tighter than the dialog. */
  maxHeight?: number
  autoFocus?: boolean
  /** What the composer says before anything is typed. It defaults to a
   *  generic prompt, which is wrong wherever several threads are stacked:
   *  the composer sits BELOW its own list, so it ends up directly above the
   *  NEXT thread's heading and reads as belonging to that one. Two downtime
   *  comments were filed a section too high that way — one against the
   *  resource instead of window 1, one against window 1 instead of window 2.
   *  Naming the target in the box itself is what makes the binding legible at
   *  the moment of typing. */
  composerPlaceholder?: string
  /** Bump to post whatever is in the composer, as if the planner had pressed
   *  Comment. The downtime dialog raises it from its own Save, because a
   *  comment is posted by Ctrl+Enter or the button and by nothing else — so a
   *  planner who typed one and hit Save lost it with no error and no row, and
   *  reasonably read that as "comments don't save". Ignored on the first
   *  render, and a no-op when the composer is empty or read-only. */
  flushSignal?: number
}

/**
 * The list of comments on one thing plus the box to add to it. Shared by the
 * Gantt popover and the downtime windows dialog, which differ only in what
 * they wrap it in.
 */
export const CommentThread = ({
  schedulerId,
  iteration,
  weekNumber,
  target,
  comments,
  readOnly = false,
  readOnlyReason,
  currentUserEmail,
  emptyText = 'No comments yet.',
  maxHeight = 232,
  autoFocus = true,
  composerPlaceholder = 'Add a comment…',
  flushSignal,
}: CommentThreadProps) => {
  const [draft, setDraft] = useState('')
  const [saving, setSaving] = useState(false)

  const targetKey = `${target.kind ?? 'production'}|${target.resourceName}|${target.productCode}|${target.startTime}`

  // A fresh target is a fresh thread — never carry a half-typed comment from
  // one batch onto another.
  useEffect(() => {
    setDraft('')
  }, [targetKey])

  const { mutate: createComment } = useCreateComment(
    () => {
      setSaving(false)
      setDraft('')
      toast.success('Comment added')
    },
    () => {
      setSaving(false)
      toast.error('Failed to add comment')
    }
  )
  const { mutate: updateComment } = useUpdateComment(
    () => {},
    () => toast.error('Failed to update comment')
  )
  const { mutate: deleteComment } = useDeleteComment(
    () => toast.success('Comment deleted'),
    () => toast.error('Failed to delete comment')
  )

  const handleResolve = useCallback(
    (commentId: string, resolved: boolean) =>
      updateComment({ schedulerId, iteration, commentId, data: { resolved } }),
    [updateComment, schedulerId, iteration]
  )
  const handleDelete = useCallback(
    (commentId: string) => deleteComment({ schedulerId, iteration, commentId }),
    [deleteComment, schedulerId, iteration]
  )

  const submit = useCallback(() => {
    const content = draft.trim()
    if (!content || readOnly) return
    setSaving(true)
    createComment({
      schedulerId,
      iteration,
      data: {
        content,
        productCode: target.productCode,
        resourceName: target.resourceName,
        resourceType: target.resourceType,
        startTime: target.startTime,
        endTime: target.endTime,
        weekNumber,
        kind: target.kind ?? 'production',
      },
    })
  }, [
    draft,
    target,
    readOnly,
    createComment,
    schedulerId,
    iteration,
    weekNumber,
  ])

  // Post-on-save. The parent bumps `flushSignal`; child effects run before the
  // parent's, so the dialog can raise it and close in the same click and this
  // still fires first. Seeded with the incoming value so mounting never counts
  // as a bump — otherwise every thread would post its (empty) draft on open.
  const flushedAt = useRef(flushSignal)
  useEffect(() => {
    if (flushSignal === flushedAt.current) return
    flushedAt.current = flushSignal
    submit()
  }, [flushSignal, submit])

  // Oldest first — a thread reads top-down, unlike the side panel's
  // newest-first feed of every comment on the schedule.
  const sorted = comments
    .slice()
    .sort((a, b) => a.createdAt.localeCompare(b.createdAt))

  return (
    <>
      <Box
        sx={{
          maxHeight,
          overflowY: 'auto',
          p: '10px 12px',
          display: 'flex',
          flexDirection: 'column',
          gap: '7px',
          bgcolor: TOKENS.panel,
        }}
      >
        {sorted.length === 0 ? (
          <Box
            sx={{
              fontSize: 11.5,
              color: TOKENS.ink400,
              textAlign: 'center',
              py: '10px',
            }}
          >
            {emptyText}
          </Box>
        ) : (
          sorted.map((c) => (
            <ThreadComment
              key={c.id}
              comment={c}
              target={target}
              isOwn={
                !!currentUserEmail &&
                c.authorEmail.toLowerCase() === currentUserEmail.toLowerCase()
              }
              readOnly={readOnly}
              onResolve={handleResolve}
              onDelete={handleDelete}
            />
          ))
        )}
      </Box>

      <Box
        sx={{ p: '9px 12px 11px', borderTop: `1px solid ${TOKENS.line200}` }}
      >
        {readOnly ? (
          <Box sx={{ fontSize: 10.5, color: TOKENS.ink400, lineHeight: 1.4 }}>
            {readOnlyReason ?? 'This schedule is read-only.'}
          </Box>
        ) : (
          <>
            <TextField
              autoFocus={autoFocus}
              multiline
              minRows={2}
              maxRows={5}
              fullWidth
              size="small"
              placeholder={composerPlaceholder}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                  e.preventDefault()
                  submit()
                }
              }}
              InputProps={{
                sx: { fontSize: 11.5, fontFamily: FONT_SANS, p: '7px 9px' },
              }}
            />
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                mt: '7px',
              }}
            >
              <Box sx={{ fontSize: 9.5, color: TOKENS.ink300 }}>
                Ctrl+Enter to post
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                {saving && <CircularProgress size={12} />}
                <PillButton
                  kind="primary"
                  size="xs"
                  onClick={submit}
                  disabled={!draft.trim() || saving}
                >
                  Comment
                </PillButton>
              </Box>
            </Box>
          </>
        )}
      </Box>
    </>
  )
}

// ─── ScheduleCommentPopover ──────────────────────────────────────────────────

export type ScheduleCommentPopoverProps = {
  schedulerId: string
  iteration: string
  weekNumber: number
  /** The batch being commented on. `null` closes the popover. */
  target: CommentTarget | null
  /** Viewport coordinates of the click that opened it. Anchored by position
   *  rather than by element on purpose: the hover pop-up the button lives in
   *  unmounts as soon as the pointer leaves it, which would strand an
   *  `anchorEl`. */
  position: { top: number; left: number } | null
  comments: SchedulerComment[]
  /** Set while an alternative answer (fastest / fewest cleans / least
   * disruption) is on screen —
   *  the thread is still readable, but a new comment would be anchored to a
   *  batch the iteration's own schedule may not have. */
  readOnly?: boolean
  readOnlyReason?: string
  currentUserEmail: string
  onClose: () => void
  /** An extra action for the batch, shown under its heading — e.g. opening it
   *  in the Model description. Omitted = no action row. */
  action?: { label: string; onClick: () => void }
}

export const ScheduleCommentPopover = ({
  schedulerId,
  iteration,
  weekNumber,
  target,
  position,
  comments,
  readOnly = false,
  readOnlyReason,
  currentUserEmail,
  onClose,
  action,
}: ScheduleCommentPopoverProps) => {
  if (!target || !position) return null

  return (
    <Popover
      open
      onClose={onClose}
      anchorReference="anchorPosition"
      anchorPosition={position}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      PaperProps={{
        sx: {
          width: 296,
          borderRadius: RADII.sm,
          border: `1px solid ${TOKENS.line200}`,
          boxShadow:
            '0 12px 32px rgba(14,27,34,0.18), 0 2px 8px rgba(14,27,34,0.08)',
          fontFamily: FONT_SANS,
        },
      }}
    >
      {/* Which batch this thread is against */}
      <Box
        sx={{
          p: '10px 12px 8px',
          borderBottom: `1px solid ${TOKENS.line200}`,
          bgcolor: TOKENS.panelHead,
        }}
      >
        <Box
          sx={{ display: 'flex', alignItems: 'center', gap: '6px', mb: '3px' }}
        >
          <ChatBubbleIcon sx={{ fontSize: 12, color: COMMENT_ACCENT }} />
          <Box
            sx={{
              fontSize: 12,
              fontWeight: 700,
              color: TOKENS.ink900,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {target.productDescription || target.productCode}
          </Box>
        </Box>
        <Box sx={{ fontFamily: FONT_MONO, fontSize: 10, color: TOKENS.ink500 }}>
          {target.resourceName} · {target.resourceType} · week {weekNumber}
        </Box>
        <Box sx={{ fontFamily: FONT_MONO, fontSize: 10, color: TOKENS.ink400 }}>
          {formatWeekHour(target.startTime)} → {formatWeekHour(target.endTime)}
        </Box>
        {action && (
          <Box
            component="button"
            onClick={action.onClick}
            sx={{
              all: 'unset',
              cursor: 'pointer',
              mt: '6px',
              fontSize: 11,
              fontWeight: 600,
              color: TOKENS.brandTeal,
              fontFamily: FONT_SANS,
            }}
          >
            {action.label} →
          </Box>
        )}
      </Box>

      <CommentThread
        schedulerId={schedulerId}
        iteration={iteration}
        weekNumber={weekNumber}
        target={target}
        comments={comments}
        readOnly={readOnly}
        readOnlyReason={readOnlyReason}
        currentUserEmail={currentUserEmail}
        emptyText="No comments on this batch yet."
      />
    </Popover>
  )
}
